import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserMinus } from 'lucide-react'
import {
  fetchNotCalledOffer,
  NOT_YOURS,
  SESSION_ENDED,
  setNotCalled,
  type AttendanceSheet,
} from '../api/attendance'
import ConfirmAction from './ConfirmAction'
import { expectANote } from '../api/fanOut'

/**
 * Declaring a sub-team not called for one session (D-851, D-855).
 *
 * `asked`: *"a teacher within the production class can go into any instance of the
 * production class ... and they can tick a box that says like will mini team be
 * included for this session ... it marks all the mini's attendance for that night as
 * not expected."*
 *
 * **Its own component because the register is opened from four places and will
 * eventually be opened from a fifth.** The kiosk register at `/room` is the one a
 * teacher actually takes (D-128, D-236) and cannot reach this yet — the declare route
 * is deliberately not `.AllowKiosk()` (D-233), so that is a `docs/GAPS.md` row rather
 * than a missing import. A panel is cheap to move; a block inlined in `RosterModal`
 * would have to be rebuilt, and two copies of one idea drift.
 *
 * **It reads the state even for somebody who cannot change it.** A teacher covering
 * the class sees *the Mini team was not called tonight* and therefore understands why
 * two thirds of her roster reads *Not called* — without it the register looks broken.
 */
/*
 * Said in full both times rather than as a bare number beside a noun, because the
 * confirm carries two counts in one sentence and *"27 dancers, 24 families"* has to
 * read as English when one of them is 1.
 */
const dancers = (n: number) => `${n} ${n === 1 ? 'dancer' : 'dancers'}`
const families = (n: number) => `${n} ${n === 1 ? 'family' : 'families'}`

export default function NotCalledPanel({
  sheet,
}: {
  sheet: AttendanceSheet
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<string | null>(null)

  /*
   * <b>Only where a call means something</b> (D-842). `takesACall` is false on all
   * but the rehearsal classes, so this is nothing at all on the other 184.
   */
  const relevant = sheet.takesACall && !sheet.isCancelled

  /*
   * <b>The counts come from the server, and only for somebody who may declare</b>
   * (D-855). A teacher who may not is shown the standing state from the sheet she
   * already has, and never this request — the count is who is on a named sub-team,
   * which is not hers to enumerate from a register.
   */
  const { data: offer, isError: offerFailed } = useQuery({
    queryKey: ['not-called-offer', sheet.occurrenceId],
    queryFn: () => fetchNotCalledOffer(sheet.occurrenceId),
    enabled: relevant && sheet.canDeclareCall,
  })

  const declare = useMutation({
    mutationFn: ({ teamId, on }: { teamId: string; on: boolean }) =>
      setNotCalled(sheet.occurrenceId, teamId, on),
    /*
     * The sheet, so every row reads *Not called*; the offer, so the control turns
     * into its own undo; and the registers list, whose "still to mark" count has
     * just moved (D-782).
     */
    onSuccess: () => {
      setConfirming(null)

      /*
       * The calendar too, since D-846 — this changed what those families see, and a
       * teacher who is also a parent is looking at the same cached week.
       */
      for (const key of [
        ['sheet'], ['not-called-offer'], ['registers'], ['class-standings'], ['calendar'],
      ]) {
        queryClient.invalidateQueries({ queryKey: key })
      }

      /*
       * <b>And the note this panel just promised</b> (D-869).
       *
       * The telling is handed off after the response, so <i>"you will get a note
       * when they have all been told"</i> is only true if something looks for it
       * — and the bell is deliberately quiet: no interval, no refetch on focus.
       * `expectANote` opens its window, and the invalidation is what starts the
       * polling: a query sitting idle never re-evaluates its own interval, so
       * without this the window would open and nothing would act on it.
       */
      expectANote()
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },

    /*
     * ⚠ <b>The dialog has to close for its refusal to be readable</b> (D-209, D-842).
     * `ConfirmAction` renders inside `Modal`, a `fixed inset-0` overlay with a focus
     * trap — so the error line below it painted *behind* the dim area while the confirm
     * button went live again. A teacher taps, nothing appears to happen, she taps
     * again, and every one of the six named refusals is thrown away. Found by an audit;
     * reachable on a lapsed cookie, a dropped connection, or the last team member being
     * removed between reading the offer and pressing the button.
     */
    onError: () => setConfirming(null),
  })

  if (!relevant) return null

  const teams = offer?.teams ?? []
  const chosen = teams.find((t) => t.teamId === confirming)

  /*
   * <b>Nothing to say and nothing to offer is not a panel</b> (D-415). A rehearsal
   * class read by a teacher who may not declare, with no declaration standing, has
   * neither.
   */
  if (!sheet.notCalledTeam && !sheet.canDeclareCall) return null

  return (
    <div className="mb-3 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
      {/*
        <b>What is being told, and that it is still going</b> (D-869).

        ⚠ This said what the send *did*, in two numbers — <i>"Emailed 23, and 21 in the
        app"</i> — which needed the whole fan-out to have finished before the request
        returned. That is eighteen seconds of provider pacing in production and about a
        hundred and forty-four on QA for two dozen households, with the confirm showing
        <i>Saving</i> throughout. `asked`: *"let that run in the background so it doesn't
        seem to be like almost locked up. And then let the owner know when all families
        have been notified."*

        So the send happens after the response and <b>this says how many it is telling</b>.
        The counts it used to print are not knowable here any more, and printing them
        would be a number true of nothing — the bell carries them instead
        (`notice.familiesTold`). The second sentence is doing real work: without it, a
        panel that stops talking looks like a panel that failed.
      */}
      {declare.data && declare.data.telling > 0 && (
        <p className="mb-2 text-[12.5px] text-ink-2">
          {/*
            No "now": this line renders off the mutation's data, which lives as
            long as the panel does - so "telling them now" would still be on
            screen five minutes after it finished. The bell is what reports the
            end; this only has to say that one is coming.
          */}
          Telling {declare.data.telling}{' '}
          {declare.data.telling === 1 ? 'family' : 'families'}. You will get a note when
          they have all been told.
        </p>
      )}

      {sheet.notCalledTeam ? (
        <p className="text-[12.5px] text-ink-2">
          <span className="font-semibold">{sheet.notCalledTeam}</span> was not called for
          this session, so those dancers read <i>Not called</i> rather than absent.
        </p>
      ) : (
        <p className="text-[12.5px] text-ink-3">
          If a sub-team was not called for this session, say so here rather than marking
          each dancer — nobody is then absent for a night they were not asked to.
        </p>
      )}

      {/*
        <b>The refusal the server would give, said before she taps</b> (D-842). The
        session being older than its own season is the one a teacher backfilling
        attendance will meet, and a control that simply failed would read as a broken
        button rather than as a rule.
      */}
      {offer?.refusal && (
        <p className="mt-1.5 text-[12px] text-ink-3">
          {offer.refusal === 'TooLongAgo'
            ? 'This session is further back than the season in progress, so it can no longer be changed.'
            : offer.refusal === 'PrivateSession'
              ? 'This is a private lesson, so there is no sub-team to leave off it.'
              : 'This class does not take a rehearsal call.'}
        </p>
      )}

      {/*
        ⚠ <b>The sheet loaded and this did not</b>, which is the ordinary shape of a
        partial failure rather than the rare one — two requests, and the client gives up
        on one after about a second (D-33, D-209). Without this the panel printed its
        invitation with no buttons under it, which reads as the feature having gone away:
        exactly the argument the empty-team button above is made on, one step over. Found
        by an audit.
      */}
      {sheet.canDeclareCall && offerFailed && (
        <p className="mt-1.5 text-[12px] text-ink-3">
          The sub-teams could not be read just now, so there is nothing to offer here yet
          &mdash; and nothing has been changed. Try again in a moment.
        </p>
      )}

      {sheet.canDeclareCall && teams.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {teams.map((team) => (
            <button
              key={team.teamId}
              type="button"
              onClick={() => setConfirming(team.teamId)}
              /*
                <b>Offered and disabled rather than hidden, when the team is
                empty</b> (D-855). A named team with nobody on it is what a studio
                reset leaves behind, and a vanished control reads as a missing
                feature — where a button saying *0 dancers* sends her to go and flag
                somebody.
              */
              disabled={!team.standing && team.covers === 0}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-60 disabled:hover:border-line-2"
            >
              <UserMinus size={14} strokeWidth={2.5} className="shrink-0" />
              {team.standing
                ? `${team.teamName} was called after all`
                : `${team.teamName} not called (${team.covers})`}
            </button>
          ))}
        </div>
      )}

      {declare.isError && (
        <p className="mt-2 text-[12.5px] text-crit">
          {(declare.error as Error).message === SESSION_ENDED
            ? 'You have been signed out, so nothing was changed. Sign in again.'
            : (declare.error as Error).message === NOT_YOURS
              ? 'This class is not yours to declare a call on, so nothing was changed.'
              : (declare.error as Error).message}
        </p>
      )}

      {/*
        <b>A second tap, through the one composition five other controls use</b>
        (D-494, D-848). The body names the team, the class, the overwrite and — since
        slice 3 — **what it is about to send**. `asked`: *"you're about to send emails
        and notifications to all the mini's."* A dialog reading *Are you sure?* makes
        somebody who mistapped confirm the thing they did not mean to do.

        ⚠ <b>Both numbers, and they are different facts</b> (`asked`, 2026-09-28).
        Sisters share a household, so the dancer count is not the family count, and the
        register in front of her shows the dancers. Naming only the families would
        misdescribe the rows she is looking at; naming only the dancers would not warn
        her she is messaging two dozen households.

        <b>And it says when it will send nothing</b> — a past rehearsal tells nobody
        (D-845), which she has to know *before* tapping, not discover afterwards.
      */}
      {chosen && (
        <ConfirmAction
          title={
            chosen.standing
              ? `${chosen.teamName} was called after all?`
              : `${chosen.teamName} not called for this session?`
          }
          body={
            (chosen.standing
              ? `${dancers(chosen.covers)} in ${sheet.className}`
                + ' go back to how they were marked before the team was declared out.'
              : `${dancers(chosen.covers)} in ${sheet.className}`
                + ' will read as not called for this session rather than absent, and any'
                + ' mark already taken for them is replaced.')
            + (chosen.families > 0
              ? ` ${families(chosen.families)} ${chosen.families === 1 ? 'is' : 'are'} told`
                + (chosen.standing ? ' that they are needed after all.' : ' by email and in the app.')
              /*
                ⚠ <b>It says nobody is told and does not say why</b> (D-857). It read
                *"this rehearsal has already been and gone"*, which is one of three reasons
                the count is nought — the others being an empty team and a future session
                whose dancers have all been marked since, which is the blessed flow for the
                undo. So on a rehearsal three days away it asserted the rehearsal was over.
                A screen confidently naming the wrong cause is worse than one naming none,
                because somebody acts on it; an audit found this one.
              */
              : ' Nobody is told.')
          }
          confirmLabel={chosen.standing ? 'They were called' : 'They were not called'}
          busyLabel="Saving"
          busy={declare.isPending}
          dismissLabel="Leave it as it is"
          onConfirm={() => declare.mutate({ teamId: chosen.teamId, on: !chosen.standing })}
          onDismiss={() => setConfirming(null)}
        />
      )}
    </div>
  )
}
